import React, { useState, useCallback, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { authHeaders } from '../auth/apiClient';

const API_URL = process.env.REACT_APP_API_BASE_URL || '/api';

/* ================================================================== */
/*  Colour / style helpers                                             */
/* ================================================================== */

const VERDICT_STYLES = {
    BUY: { bg: '#dcfce7', fg: '#166534', border: '#22c55e' },
    HOLD: { bg: '#fef9c3', fg: '#854d0e', border: '#eab308' },
    AVOID: { bg: '#fee2e2', fg: '#991b1b', border: '#ef4444' },
};

const scoreColor = (value, invert = false) => {
    const v = invert ? 100 - value : value;
    if (v >= 65) return '#22c55e';
    if (v >= 40) return '#eab308';
    return '#ef4444';
};

const scoreLabelStyle = (label) => {
    const map = {
        High: { bg: '#dcfce7', fg: '#166534' },
        Medium: { bg: '#fef9c3', fg: '#854d0e' },
        Low: { bg: '#fee2e2', fg: '#991b1b' },
        Attractive: { bg: '#dcfce7', fg: '#166534' },
        Neutral: { bg: '#fef9c3', fg: '#854d0e' },
        Unattractive: { bg: '#fee2e2', fg: '#991b1b' },
        Moderate: { bg: '#fef9c3', fg: '#854d0e' },
    };
    return map[label] || { bg: '#f3f4f6', fg: '#374151' };
};

/* ================================================================== */
/*  Sub-components                                                     */
/* ================================================================== */

/* ---- Verdict banner ---- */
const VerdictBanner = ({ verdict, rationale, companyName }) => {
    const style = VERDICT_STYLES[verdict] || VERDICT_STYLES.HOLD;
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 16,
            padding: '16px 24px', borderRadius: 12,
            background: style.bg, border: `2px solid ${style.border}`,
            marginBottom: 24,
        }}>
            <span style={{
                fontSize: 28, fontWeight: 800, color: style.fg,
                letterSpacing: 2, minWidth: 70,
            }}>
                {verdict}
            </span>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: style.fg, marginBottom: 2 }}>
                    {companyName}
                </div>
                <div style={{ fontSize: 13, color: style.fg, opacity: 0.85, lineHeight: 1.5 }}>
                    {rationale}
                </div>
            </div>
        </div>
    );
};

/* ---- Score tiles row ---- */
const ScoreTile = ({ title, score, invertColor }) => (
    <div style={{
        flex: '1 1 180px', background: 'var(--surface-1, #fff)',
        borderRadius: 10, padding: '16px 12px', textAlign: 'center',
        border: '1px solid var(--border-1, #e5e7eb)', minWidth: 160,
    }}>
        <div style={{ fontSize: 12, color: 'var(--text-2, #6b7280)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {title}
        </div>
        <div style={{
            fontSize: 32, fontWeight: 700,
            color: scoreColor(score.value, invertColor), lineHeight: 1.1,
        }}>
            {score.value}
        </div>
        <div style={{ marginTop: 6 }}>
            <span style={{
                padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                backgroundColor: scoreLabelStyle(score.label).bg,
                color: scoreLabelStyle(score.label).fg,
            }}>
                {score.label}
            </span>
        </div>
    </div>
);

const ScoreTiles = ({ scores }) => {
    if (!scores) return null;
    const tiles = [
        { key: 'forwardLooking', title: 'Forward-Looking', invertColor: false },
        { key: 'operatingPerformance', title: 'Operating', invertColor: false },
        { key: 'valuationAttractiveness', title: 'Valuation', invertColor: false },
        { key: 'riskRedFlag', title: 'Risk / Red Flags', invertColor: true },
    ];
    return (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
            {tiles.map(t => scores[t.key] && (
                <ScoreTile key={t.key} title={t.title} score={scores[t.key]} invertColor={t.invertColor} />
            ))}
        </div>
    );
};

/* ---- Key takeaways (strengths / risks / exit signals) ---- */
const TakeawayList = ({ items, icon, title, color }) => {
    if (!items?.length) return null;
    return (
        <div style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {icon} {title}
            </h4>
            <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7, fontSize: 14, color: 'var(--text-1, #374151)' }}>
                {items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
        </div>
    );
};

/* ---- Buffett checklist table ---- */
const BuffettChecklist = ({ checklist }) => {
    if (!checklist?.length) return null;
    const answerStyle = (ans) => {
        if (ans === 'Yes') return { bg: '#dcfce7', fg: '#166534' };
        if (ans === 'No') return { bg: '#fee2e2', fg: '#991b1b' };
        return { bg: '#fef9c3', fg: '#854d0e' };
    };
    return (
        <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-1, #e5e7eb)' }}>
                        <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-2, #6b7280)' }}>Question</th>
                        <th style={{ textAlign: 'center', padding: '8px 12px', color: 'var(--text-2, #6b7280)', width: 80 }}>Answer</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-2, #6b7280)' }}>Detail</th>
                    </tr>
                </thead>
                <tbody>
                    {checklist.map((item, i) => {
                        const as = answerStyle(item.answer);
                        return (
                            <tr key={i} style={{ borderBottom: '1px solid var(--border-1, #f3f4f6)' }}>
                                <td style={{ padding: '8px 12px', fontWeight: 500 }}>{item.question}</td>
                                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                    <span style={{
                                        padding: '2px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                                        backgroundColor: as.bg, color: as.fg,
                                    }}>
                                        {item.answer}
                                    </span>
                                </td>
                                <td style={{ padding: '8px 12px', color: 'var(--text-2, #6b7280)' }}>{item.detail}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

/* ---- Report section (collapsible) ---- */
const ReportSection = ({ section, isOpen, onToggle }) => {
    return (
        <div style={{
            marginBottom: 12, borderRadius: 10,
            border: '1px solid var(--border-1, #e5e7eb)',
            background: 'var(--surface-1, #fff)',
            overflow: 'hidden',
        }}>
            <button
                onClick={onToggle}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    padding: '14px 20px', border: 'none', background: 'transparent',
                    cursor: 'pointer', textAlign: 'left',
                }}
            >
                <span style={{
                    fontSize: 12, transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s', color: 'var(--text-3, #9ca3af)',
                }}>
                    ▶
                </span>
                <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1, #111)' }}>
                    {section.title}
                </span>
            </button>
            {isOpen && (
                <div style={{
                    padding: '0 20px 20px 20px',
                    fontSize: 14, lineHeight: 1.75,
                    color: 'var(--text-1, #374151)',
                }}>
                    <div className="report-markdown">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {section.content}
                        </ReactMarkdown>
                    </div>

                    {/* Render buffett checklist inline if this is that section */}
                    {section.id === 'buffett_checklist' && section._checklist && (
                        <BuffettChecklist checklist={section._checklist} />
                    )}
                </div>
            )}
        </div>
    );
};

/* ---- Table of contents sidebar ---- */
const TOC = ({ sections, activeSection, onJump }) => (
    <div style={{
        position: 'sticky', top: 16, width: 220, flexShrink: 0,
        fontSize: 12, color: 'var(--text-2, #6b7280)',
    }}>
        <div style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, color: 'var(--text-3, #9ca3af)' }}>
            Report Sections
        </div>
        {sections.map((s, i) => (
            <div
                key={s.id}
                onClick={() => onJump(s.id)}
                style={{
                    padding: '5px 8px', borderRadius: 4, cursor: 'pointer',
                    fontWeight: activeSection === s.id ? 600 : 400,
                    color: activeSection === s.id ? 'var(--text-1, #111)' : 'var(--text-2, #6b7280)',
                    background: activeSection === s.id ? 'var(--surface-2, #f3f4f6)' : 'transparent',
                    marginBottom: 2, lineHeight: 1.6,
                }}
            >
                {i + 1}. {s.title}
            </div>
        ))}
    </div>
);

/* ================================================================== */
/*  Main component                                                     */
/* ================================================================== */

const InvestmentIntelligenceTab = ({ companyId, basis }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openSections, setOpenSections] = useState({});

    // Fetch cached report on mount
    const fetchReport = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/investment-intelligence?companyId=${companyId}&basis=${basis}`);
            const payload = await res.json();
            if (payload.success && payload.data) {
                setData(payload.data);
                setError(null);
                // Auto-open all sections
                const report = payload.data.report || {};
                const sections = report.sections || [];
                const open = {};
                sections.forEach(s => { open[s.id] = true; });
                setOpenSections(open);
            } else {
                setData(null);
            }
        } catch {
            setData(null);
        }
    }, [companyId, basis]);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    const runAnalysis = async (forceRefresh = false) => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/investment-intelligence/run`, {
                method: 'POST',
                headers: await authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ companyId, basis, forceRefresh }),
            });
            const payload = await res.json();
            if (payload.success && payload.data) {
                setData(payload.data);
                const report = payload.data.report || {};
                const sections = report.sections || [];
                const open = {};
                sections.forEach(s => { open[s.id] = true; });
                setOpenSections(open);
            } else {
                setError(payload.message || 'Analysis failed');
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleSection = (id) => {
        setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleAll = (open) => {
        if (!report?.sections) return;
        const next = {};
        report.sections.forEach(s => { next[s.id] = open; });
        setOpenSections(next);
    };

    const jumpToSection = (id) => {
        setOpenSections(prev => ({ ...prev, [id]: true }));
        const el = document.getElementById(`report-section-${id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const report = data?.report;
    const sections = useMemo(() => {
        if (!report?.sections) return [];
        return report.sections.map(s => {
            if (s.id === 'buffett_checklist' && report.buffettChecklist) {
                return { ...s, _checklist: report.buffettChecklist };
            }
            return s;
        });
    }, [report]);

    /* ---------- Empty state ---------- */
    if (!data && !loading && !error) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '80px 20px', textAlign: 'center',
                color: 'var(--text-2, #6b7280)',
            }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
                <h3 style={{ marginBottom: 8, color: 'var(--text-1, #111)', fontSize: 20 }}>
                    Investment Intelligence Report
                </h3>
                <p style={{ maxWidth: 480, marginBottom: 24, lineHeight: 1.7, fontSize: 14 }}>
                    Generate a comprehensive investment research report with executive summary,
                    moat analysis, financial deep-dive, forensic checks, valuation,
                    Buffett checklist, and a final BUY / HOLD / AVOID verdict.
                </p>
                <button
                    onClick={() => runAnalysis(false)}
                    disabled={loading}
                    style={{
                        padding: '12px 36px', borderRadius: 8, border: 'none',
                        background: '#2563eb', color: '#fff', fontWeight: 600,
                        fontSize: 15, cursor: loading ? 'wait' : 'pointer',
                    }}
                >
                    Generate Report
                </button>
            </div>
        );
    }

    /* ---------- Loading ---------- */
    if (loading) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: 80, color: 'var(--text-2, #6b7280)',
            }}>
                <div style={{ fontSize: 36, marginBottom: 16, animation: 'pulse 1.5s infinite' }}>📝</div>
                <div style={{ fontSize: 15, fontWeight: 500 }}>Generating investment research report…</div>
                <div style={{ fontSize: 13, marginTop: 8, color: '#9ca3af' }}>
                    Analysing ratios, forensic data, peers & generating narrative. This may take 30-60 seconds.
                </div>
            </div>
        );
    }

    /* ---------- Error ---------- */
    if (error) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <div style={{ color: '#dc2626', marginBottom: 12, fontSize: 14 }}>{error}</div>
                <button
                    onClick={() => runAnalysis(true)}
                    style={{
                        padding: '8px 20px', borderRadius: 6, border: '1px solid #dc2626',
                        background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: 13,
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    /* ---------- Report ---------- */
    if (!report) {
        return <div style={{ padding: 20, color: 'var(--text-2)' }}>No report data available.</div>;
    }

    return (
        <div style={{ padding: '0 4px' }}>
            {/* ---- Header bar ---- */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: 16,
            }}>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                    Generated {data.generatedAt ? new Date(data.generatedAt).toLocaleString() : ''}
                    {report._model && <span style={{ marginLeft: 8 }}>· {report._model}</span>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => toggleAll(true)} style={tinyBtnStyle}>Expand all</button>
                    <button onClick={() => toggleAll(false)} style={tinyBtnStyle}>Collapse all</button>
                    <button onClick={() => runAnalysis(true)} disabled={loading} style={{
                        ...tinyBtnStyle, background: '#2563eb', color: '#fff', border: 'none',
                    }}>
                        ↻ Regenerate
                    </button>
                </div>
            </div>

            {/* ---- Verdict banner ---- */}
            <VerdictBanner
                verdict={report.verdict || 'HOLD'}
                rationale={report.verdictRationale || ''}
                companyName={report.companyName || data.companyName || ''}
            />

            {/* ---- Score tiles ---- */}
            <ScoreTiles scores={report.scores} />

            {/* ---- Key takeaways row ---- */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
                <div style={{ flex: '1 1 280px', padding: 16, borderRadius: 10, border: '1px solid var(--border-1, #e5e7eb)', background: 'var(--surface-1, #fff)' }}>
                    <TakeawayList items={report.keyStrengths} icon="✅" title="Key Strengths" color="#166534" />
                    <TakeawayList items={report.keyRisks} icon="⚠️" title="Key Risks" color="#991b1b" />
                </div>
                <div style={{ flex: '1 1 280px', padding: 16, borderRadius: 10, border: '1px solid var(--border-1, #e5e7eb)', background: 'var(--surface-1, #fff)' }}>
                    <TakeawayList items={report.exitSignals} icon="🚪" title="Exit Signals" color="#854d0e" />
                </div>
            </div>

            {/* ---- Report body (TOC + sections) ---- */}
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
                {/* TOC sidebar — hidden on narrow screens */}
                <div style={{ display: sections.length > 3 ? 'block' : 'none' }} className="report-toc-sidebar">
                    <TOC sections={sections} onJump={jumpToSection} />
                </div>

                {/* Sections */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {sections.map((section) => (
                        <div key={section.id} id={`report-section-${section.id}`}>
                            <ReportSection
                                section={section}
                                isOpen={!!openSections[section.id]}
                                onToggle={() => toggleSection(section.id)}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* ---- Inline styles for markdown rendering ---- */}
            <style>{`
                .report-markdown h1, .report-markdown h2, .report-markdown h3, .report-markdown h4 {
                    margin-top: 16px; margin-bottom: 8px;
                    color: var(--text-1, #111);
                }
                .report-markdown h3 { font-size: 14px; font-weight: 700; }
                .report-markdown h4 { font-size: 13px; font-weight: 600; }
                .report-markdown p { margin: 8px 0; }
                .report-markdown ul, .report-markdown ol {
                    margin: 8px 0; padding-left: 20px;
                }
                .report-markdown li { margin-bottom: 4px; }
                .report-markdown table {
                    width: 100%; border-collapse: collapse;
                    margin: 12px 0; font-size: 13px;
                }
                .report-markdown th {
                    text-align: left; padding: 8px 12px;
                    border-bottom: 2px solid var(--border-1, #e5e7eb);
                    color: var(--text-2, #6b7280); font-weight: 600;
                }
                .report-markdown td {
                    padding: 6px 12px;
                    border-bottom: 1px solid var(--border-1, #f3f4f6);
                }
                .report-markdown tr:hover td {
                    background: var(--surface-2, #f9fafb);
                }
                .report-markdown strong { color: var(--text-1, #111); }
                .report-markdown blockquote {
                    border-left: 3px solid var(--border-1, #d1d5db);
                    padding: 4px 12px; margin: 12px 0;
                    color: var(--text-2, #6b7280); font-style: italic;
                }
                .report-toc-sidebar {
                    display: block;
                }
                @media (max-width: 900px) {
                    .report-toc-sidebar { display: none !important; }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
        </div>
    );
};

const tinyBtnStyle = {
    fontSize: 11, padding: '4px 12px', borderRadius: 5,
    border: '1px solid var(--border-1, #d1d5db)', background: 'transparent',
    cursor: 'pointer', color: 'var(--text-2, #374151)',
};

export default InvestmentIntelligenceTab;
